import React, { useState } from 'react';
import { X } from 'lucide-react';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Phase 12 — scheduling/limit controls. Unlike CRM mapping/webhooks/
 * notifications (all schema.settings, versioned with the form), these live
 * directly on the Form document (openAt/closeAt/maxSubmissions/
 * submissionCount) since they govern whether the *currently published*
 * form accepts submissions right now, independent of schema edits. Saved
 * immediately via PUT /forms/:id/availability rather than folded into the
 * builder's own save/publish flow.
 */
export default function AvailabilityPanel({ form, onSave, onClose }) {
  const [openAt, setOpenAt] = useState(toLocalInputValue(form.openAt));
  const [closeAt, setCloseAt] = useState(toLocalInputValue(form.closeAt));
  const [maxSubmissions, setMaxSubmissions] = useState(form.maxSubmissions ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        openAt: openAt ? new Date(openAt).toISOString() : null,
        closeAt: closeAt ? new Date(closeAt).toISOString() : null,
        maxSubmissions: maxSubmissions === '' ? null : Number(maxSubmissions),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const overLimit = form.maxSubmissions != null && form.submissionCount >= form.maxSubmissions;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[440px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Scheduling & Limits</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelClass}>Opens At</label>
            <input type="datetime-local" className={inputClass} value={openAt} onChange={(e) => setOpenAt(e.target.value)} />
            <p className="text-[11px] text-[#706E6B] mt-1">Leave blank to accept submissions immediately once published.</p>
          </div>

          <div>
            <label className={labelClass}>Closes At</label>
            <input type="datetime-local" className={inputClass} value={closeAt} onChange={(e) => setCloseAt(e.target.value)} />
            <p className="text-[11px] text-[#706E6B] mt-1">Leave blank to keep accepting submissions indefinitely.</p>
          </div>

          <div>
            <label className={labelClass}>Max Submissions</label>
            <input
              type="number"
              min="1"
              className={inputClass}
              placeholder="Unlimited"
              value={maxSubmissions}
              onChange={(e) => setMaxSubmissions(e.target.value)}
            />
            <p className={`text-[11px] mt-1 ${overLimit ? 'text-[#EA001E]' : 'text-[#706E6B]'}`}>
              {form.submissionCount || 0} submission{form.submissionCount === 1 ? '' : 's'} received so far
              {form.maxSubmissions != null && ` (limit: ${form.maxSubmissions})`}.
              {overLimit && ' Limit reached — raise it or clear it to accept more.'}
            </p>
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="mt-6 w-full px-4 h-9 rounded bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold disabled:opacity-60 transition-colors"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
